// React
import React, { useState, useContext, useRef, useEffect } from 'react';

// Styles
import styles from './Filter.module.scss';

// Third party
import PropTypes from 'prop-types';
import nextId from 'react-id-generator';
import { trackPromise } from 'react-promise-tracker';

// Material UI
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText
} from '@material-ui/core';

// Contexts
import { ListingContext } from '../../../contexts/listing';

// Services
import { getListingData } from '../../../services/taxonomies';

// Shared
import eventBus from '../../../shared/eventBus';

// Hooks
import useMounted from '../../../hooks/useMounted';

// This component
export default function Filter({
  type,
  label,
  parameter,
  staticData,
  endpoint,
  getRows,
  disabled,
  setDisabled,
  isSelectLabelAsValue = false,
  isPlaceholderRendered = true
}) {
  // Refeference (input)
  const input = useRef(null);

  // Listing Context
  const { initialFilters, activeFilters, saveActiveFilters } = useContext(ListingContext);

  // Helper function to get and prepare the value
  const getValue = () => {
    let result = activeFilters && activeFilters.hasOwnProperty(parameter) ? activeFilters[parameter] : '';
    if (type === 'multiselect') {
      result = result.split('|'); // the values will be split by '|' character
    }
    return result;
  };
  // Field value
  const initialValue = useRef(null);

  const [value, setValue] = useState(getValue);

  // Disable next field (used only on "duo" type)
  const [disableNextField, setDisableNextField] = useState(true);

  // If the component is mounted
  const mounted = useMounted();

  // Data
  const [data, setData] = useState(
    staticData || {
      data: []
    }
  );

  // Keep reference to the inital passed function (we don't need to watch its changes, just need to invoke)
  const getRowsRef = useRef(getRows);

  // Handle change
  const handleChange = (event) => {
    const newValue = event.target.value;
    const name = event.target.name;

    let copy = JSON.parse(JSON.stringify(activeFilters));

    if (type === 'multiselect') {
      const indexOfEmptyVal = newValue.indexOf('');
      if (indexOfEmptyVal > -1) {
        newValue.splice(indexOfEmptyVal, 1);
      }

      if (newValue.length === 0) {
        delete copy[name];
      } else {
        copy[name] = newValue.join('|'); // activeFilters is passed as string like a|b|c
      }
    } else {
      if (newValue === '') {
        delete copy[name];
      } else {
        copy[name] = newValue;
      }
    }

    setValue(newValue);
    saveActiveFilters(copy);
  };

  // Set the initial (default) filter value variable
  useEffect(() => {
    if (initialValue.current === null) {
      let result = initialFilters && initialFilters.hasOwnProperty(parameter) ? initialFilters[parameter] : '';
      if (type === 'multiselect') {
        result = result.split('|'); // the values will be split by '|' character
      }
      initialValue.current = result;
    }
  });

  // Component did mount (reset filters)
  useEffect(() => {
    // Receive global event to reset all filters
    eventBus.on('resetFilters', (data_) => {
      if (mounted.current && data_.reset && typeof parameter == 'string') {
        setValue(initialValue.current);

        if (type === 'text') {
          input.current.value = initialValue.current;
        }
      }
    });
  }, [parameter, type, mounted]);

  // Component did mount (get data)
  useEffect(() => {
    // Dynamic data
    if ((type === 'select' || type === 'multiselect') && endpoint) {
      const promise = getListingData({
        endpoint,
        getRows: getRowsRef.current
      });

      trackPromise(
        promise.then((newData) => {
          if (
            mounted.current &&
            newData &&
            newData.data &&
            newData.data.length
          ) {
            setData(newData);
          }
        })
      );
    }
  }, [type, endpoint, mounted]);

  // Component did mount ("duo" type)
  useEffect(() => {
    if (typeof setDisabled == 'function') {
      setDisabled(value === '');
    }
  }, [value, setDisabled]);

  // Select
  if (type === 'select') {
    const uniqueId = nextId('tps-unique-id-');

    return (
      <FormControl variant="outlined" fullWidth={true}>
        <InputLabel id={uniqueId}>{label}</InputLabel>
        <Select
          inputRef={input}
          classes={{ select: styles.select }}
          labelId={uniqueId}
          name={parameter}
          label={label}
          value={value === undefined || value === null || !data.data || data.data.length === 0 ? '' : value}
          onChange={handleChange}>
          {
            isPlaceholderRendered &&
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
          }
          {data.data &&
            data.data.map((item, i) => {
              return (
                <MenuItem key={i} value={isSelectLabelAsValue ? item.label : item.value}>
                  {item.label}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    );
  }

  // Milti select
  else if (type === 'multiselect') {
    const uniqueId = nextId('tps-unique-id-');

    return (
      <FormControl variant="outlined" fullWidth={true}>
        <InputLabel id={uniqueId}>{label}</InputLabel>
        <Select
          multiple={true}
          inputRef={input}
          classes={{ select: styles.select }}
          labelId={uniqueId}
          name={parameter}
          label={label}
          value={value === undefined || value === null || !data.data || data.data.length === 0 ? [] : value}
          onChange={handleChange}
          renderValue={(selected) => selected.join(', ')}>
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {data.data &&
            data.data.map((item, i) => {
              return (
                <MenuItem key={i} value={isSelectLabelAsValue ? item.label : item.value}>
                  <Checkbox checked={value.indexOf(item.value) > -1}/>
                  <ListItemText primary={item.label}/>
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    );
  }

  // Duo field
  else if (type === 'duo') {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={5}>
          <Filter
            type="select"
            parameter={parameter[0]}
            label={label}
            staticData={{
              data: [
                {
                  label: 'Contains',
                  value: 'C'
                },
                {
                  label: 'Starts With',
                  value: 'S'
                },
                {
                  label: 'Ends With',
                  value: 'F'
                },
                {
                  label: 'Equals',
                  value: 'E'
                }
              ]
            }}
            setDisabled={setDisableNextField}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={7}>
          <Filter
            type="text"
            parameter={parameter[1]}
            label={label}
            disabled={disableNextField}
          />
        </Grid>
      </Grid>
    );
  }

  // Text Field (deafult)
  return (
    <TextField
      inputRef={input}
      className={styles['input-text']}
      variant="outlined"
      name={parameter}
      label={label}
      fullWidth={true}
      onBlur={handleChange}
      disabled={disabled === true}
      defaultValue={value}
    />
  );
}

// Properties
Filter.propTypes = {
  type: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  parameter: PropTypes.any.isRequired
};
